import { yupResolver } from '@hookform/resolvers/yup'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material'
import { DateTimePicker } from '@mui/x-date-pickers'
import { debounce } from 'lodash'
import { DateTime } from 'luxon'
import { useEffect, useState } from 'react'
import { Controller, useForm, WatchObserver } from 'react-hook-form'
import { parseType } from 'src/functions/engagement'
import { FragmentType, useFragment } from 'src/gql'
import { EngagementType } from 'src/gql/graphql'
import { selectEngagements } from 'src/store/selectors/autosave'
import { clearEngagement, saveEngagement } from 'src/store/slices/autosave'
import { useAppDispatch } from 'src/store/useAppDispatch'
import { useAppSelector } from 'src/store/useAppSelector'
import * as yup from 'yup'

import { ControlledColoredSwitch } from '../Inputs/Controlled/ColoredSwitch'
import { ControlledSelect } from '../Inputs/Controlled/Select'
import { StatefulButton } from '../StatefulButton'
import { EngagementFormValues } from './types'
import { EngagementFragment, useEngagements } from './useEngagements'

interface Props {
  buttonComponent: React.ReactNode
  memberId: number
  engagement?: FragmentType<typeof EngagementFragment>
  positionLeft?: boolean
  defaultStartedAt?: DateTime
  defaultType?: EngagementType
}

export const EngagementModal: React.FC<Props> = props => {
  const { memberId, positionLeft, defaultStartedAt, defaultType } = props
  const engagement = useFragment(EngagementFragment, props.engagement)
  const autosaveKey = `member:${memberId}-engagement:${engagement?.id}`
  const [open, setOpen] = useState(false)
  const { create, update } = useEngagements()
  const dispatch = useAppDispatch()
  const autosavedEngagement = useAppSelector(selectEngagements)[autosaveKey]
  const baseValues: EngagementFormValues = {
    duration: engagement?.duration ?? NullEngagement.duration,
    type: engagement?.type ?? defaultType ?? NullEngagement.type,
    successful: engagement?.successful ?? NullEngagement.successful,
    notes: engagement?.notes ?? NullEngagement.notes,
    planChanges: engagement?.planChanges ?? NullEngagement.planChanges,
    goalProgress: engagement?.goalProgress ?? NullEngagement.goalProgress,
    referrals: engagement?.referrals ?? NullEngagement.referrals,
    actions: engagement?.actions ?? NullEngagement.actions,
    startedAt:
      engagement?.startedAt ?? defaultStartedAt ?? DateTime.local(),
  }

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
    watch,
    reset,
  } = useForm<EngagementFormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      ...baseValues,
      ...autosavedEngagement,
      startedAt: autosavedEngagement
        ? DateTime.fromISO(
            autosavedEngagement.startedAt ?? new Date().toISOString(),
          )
        : baseValues.startedAt,
    },
  })

  const onSubmit = async (values: EngagementFormValues) => {
    const input = schema.validateSync(values)
    if (engagement) {
      const { id } = engagement
      const { data } = await update(id, { ...input, memberId: +memberId })
      if (data?.updateEngagement.success) setOpen(false)
    } else {
      const { data } = await create({ ...input, memberId: +memberId })
      if (data?.createEngagement.success ?? false) {
        reset(baseValues)
        dispatch(clearEngagement(autosaveKey))
        setOpen(false)
      }
    }
  }

  const onCancel = async () => {
    dispatch(clearEngagement(autosaveKey))
    reset(baseValues)
    setOpen(false)
  }

  useEffect(() => {
    const debouncedWatchCallback = debounce(
      ({ startedAt: startedAtDT, ...data }) => {
        const startedAt = (startedAtDT as DateTime)?.toISO?.()
        dispatch(
          saveEngagement({
            key: autosaveKey,
            engagement: { ...data, startedAt },
          }),
        )
      },
      3000,
      { maxWait: 30000 },
    ) as WatchObserver<EngagementFormValues>
    const subscription = watch(debouncedWatchCallback)

    return () => subscription.unsubscribe()
  }, [watch, autosaveKey, dispatch])

  return (
    <Box>
      <Box onClick={() => setOpen(true)}>{props.buttonComponent}</Box>
      <Dialog
        open={open}
        fullWidth
        maxWidth="md"
        sx={
          positionLeft
            ? {
                '& .MuiDialog-container': {
                  justifyContent: 'flex-start',
                  alignItems: 'flex-start',
                },
                '& .MuiDialog-paper': {
                  m: 2,
                  maxHeight: 'calc(100% - 32px)',
                  maxWidth: 'min(900px, max(560px, calc(100vw - 724px)))',
                },
              }
            : undefined
        }>
        <DialogTitle>{engagement ? 'Edit' : 'New'} Engagement</DialogTitle>
        <DialogContent>
          <Box
            display="flex"
            flexDirection="column"
            mt={t => t.spacing(2)}
            sx={t => ({
              '& > *:not(:last-child)': { marginBottom: t.spacing(2) },
            })}>
            <Box
              display="flex"
              flexDirection="row"
              sx={t => ({
                '& > *:not(:last-child)': { marginRight: t.spacing(2) },
              })}>
              <FormControl fullWidth>
                <Controller
                  name="startedAt"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <DateTimePicker
                      {...field}
                      label="Date"
                      slotProps={{
                        textField: {
                          error: !!error,
                          helperText: error?.message,
                        },
                      }}
                    />
                  )}
                />
              </FormControl>
              <Controller
                name="duration"
                control={control}
                render={({ field: { onChange, onBlur, value } }) => (
                  <>
                    <FormControl fullWidth>
                      <InputLabel htmlFor="hours-select" id="hours-input-label">
                        Hours
                      </InputLabel>
                      <Select
                        inputProps={{ id: 'hours-select' }}
                        labelId="hours-select-label"
                        label="Hours"
                        error={!!errors.duration}
                        value={Math.floor(value / 60)}
                        onBlur={onBlur}
                        onChange={e => {
                          const duration = +e.target.value * 60 + (value % 60)
                          onChange(duration)
                        }}>
                        {[0, 1, 2, 3, 4, 5, 6, 7, 8].map(value => (
                          <MenuItem key={value} value={value}>
                            {value}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl fullWidth>
                      <InputLabel
                        htmlFor="minutes-select"
                        id="minutes-input-label">
                        Minutes
                      </InputLabel>
                      <Select
                        inputProps={{ id: 'minutes-select' }}
                        labelId="minutes-select-label"
                        label="Minutes"
                        name="minutes"
                        error={!!errors.duration}
                        value={value % 60}
                        onChange={e => {
                          const duration =
                            +e.target.value + Math.floor(value / 60) * 60
                          onChange(duration)
                        }}>
                        {[1, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60].map(
                          value => (
                            <MenuItem key={value} value={value}>
                              {value}
                            </MenuItem>
                          ),
                        )}
                      </Select>
                      <FormHelperText error>
                        {errors.duration?.message}
                      </FormHelperText>
                    </FormControl>
                  </>
                )}
              />
            </Box>
            <Box
              display="flex"
              flexDirection="row"
              sx={t => ({
                '& > *:not(:last-child)': { marginRight: t.spacing(2) },
              })}>
              <ControlledSelect
                fullWidth
                control={control}
                name="type"
                label="Engagement Type"
                error={errors.type}
                options={Object.values(EngagementType).map(t => ({
                  key: t,
                  value: t,
                  label: parseType(t),
                }))}
              />
              <FormControl fullWidth>
                <FormControlLabel
                  label="Engagement Successful?"
                  labelPlacement="top"
                  sx={{ alignItems: 'start' }}
                  control={
                    <ControlledColoredSwitch
                      control={control}
                      name="successful"
                    />
                  }
                />
              </FormControl>
            </Box>
            <FormControl>
              <TextField
                {...register('planChanges')}
                label="Changes to plan"
                multiline
              />
            </FormControl>
            <FormControl>
              <TextField
                {...register('goalProgress')}
                label="Progress on goals"
                multiline
              />
            </FormControl>
            <FormControl>
              <TextField {...register('actions')} label="Actions" multiline />
            </FormControl>
            <FormControl>
              <TextField
                {...register('referrals')}
                label="Referrals & Resources"
                multiline
              />
            </FormControl>
            <FormControl>
              <TextField
                {...register('notes')}
                label="Engagement Notes"
                multiline
                rows="6"
              />
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            sx={t => ({ marginRight: t.spacing(1) })}
            variant="outlined"
            size="large"
            color="primary"
            onClick={onCancel}>
            Cancel
          </Button>
          <StatefulButton
            onClick={handleSubmit(onSubmit)}
            loading={isSubmitting}
            variant="contained"
            size="large"
            color="primary"
            type="submit">
            Save
          </StatefulButton>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

const schema = yup.object().shape({
  duration: yup
    .number()
    .positive('Duration must be longer than 0 minutes')
    .integer()
    .required('Required'),
  type: yup
    .mixed<EngagementType>()
    .required('Required')
    .oneOf(Object.values(EngagementType)),
  successful: yup.boolean().required('Required'),
  startedAt: yup.mixed<DateTime>().required('Engagement date required'),
  notes: yup
    .string()
    .nullable()
    .transform(value => (!value ? null : value)),
  planChanges: yup
    .string()
    .nullable()
    .transform(value => (!value ? null : value)),
  goalProgress: yup
    .string()
    .nullable()
    .transform(value => (!value ? null : value)),
  referrals: yup
    .string()
    .nullable()
    .transform(value => (!value ? null : value)),
  actions: yup
    .string()
    .nullable()
    .transform(value => (!value ? null : value)),
})

const NullEngagement: EngagementFormValues = {
  duration: 1,
  type: EngagementType.PhoneCall,
  successful: true,
  notes: '',
  planChanges: '',
  goalProgress: '',
  referrals: '',
  actions: '',
  startedAt: DateTime.local(),
}
